import classnames from "classnames";
import format from "date-fns/format";
import { graphql, Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React from "react";

import Video from "../video/Video";
import * as styles from "./ItemTile.module.css";

const ItemTile = ({
  className,
  image,
  video,
  path,
  title,
  description,
  children,
  backgroundColor,
  height,
  style,
  lightOnDark,
  overlayImage,
  empty,
  timeToRead,
  publishedDate,
  isPost,
  onClick,
  remoteImage,
  marginBottom
}) => {
  const classes = classnames(styles.tile, className, {
    [styles.lightOnDark]: lightOnDark,
    [styles.extraSmallHeight]: height === "extraSmall",
    [styles.smallHeight]: height === "small",
    [styles.mediumHeight]: height === "medium",
    [styles.largeHeight]: height === "large",
    [styles.extraLargeHeight]: height === "extraLarge",
    [styles.tallHeight]: height === "tall",
    [styles.empty]: empty,
    [styles.marginBottom]: marginBottom
  });

  let text = "";

  if (!!description) {
    if (typeof description === "string") {
      text = <div className={`${styles.tileDescription} smallestBodyText`}>{description}</div>;
    } else if (description.childMarkdownRemark !== undefined) {
      text = (
        <div
          className={`${styles.tileDescription} smallestBodyText`}
          dangerouslySetInnerHTML={{
            __html: description.childMarkdownRemark.html,
          }}
        />
      );
    }
  }

  const renderInner = (
    <div className={styles.text}>
      {typeof title === "string" && (
        <div className={`${styles.tileTitle} h3`} style={{ marginTop: "10px", marginBottom: "4px" }}>
          {title}
        </div>
      )}
      {text}
      {isPost && (
        <p className="smallestBodyText">
          {publishedDate && format(new Date(publishedDate), "d MMM, yyyy")}&nbsp;·&nbsp;
          {timeToRead} min read
        </p>
      )}
      <p className="smallBodyText">{children}</p>
    </div>
  );

  let renderMedia;
  if (video) {
    renderMedia = (
      <Video video={video.video?.file.url} posterVideo={video?.poster?.publicUrl} />
    );
  } else if (image) {
    renderMedia = (
      <GatsbyImage
        image={image.gatsbyImageData}
        alt={image.title || "Image"}
        className={`${styles.tileImage} borderRadius`}
      />
    );
  } else {
    renderMedia = <div className={`${styles.tileImage} borderRadius`} />;
  }

  return (
    <div className={classes}>
      {path ? (
        <Link
          to={path || "/"}
          style={(style, { backgroundColor })}
          className={styles.tileLink}
          onClick={onClick}
        >
          {image && (
            <div className={`${styles.imageHoverWrapper} borderRadius`}>
              <div className={styles.imageHoverInner}>
                <GatsbyImage
                  image={image.gatsbyImageData}
                  alt={image.title || ""}
                  className={styles.tileImage}
                  style={style}
                />
              </div>
              {overlayImage && overlayImage}
            </div>
          )}
          {remoteImage ? remoteImage : null}
          {renderInner}
        </Link>
      ) : (
        <div>
          {renderMedia}
          {renderInner}
        </div>
      )}
    </div>
  );
};

export default ItemTile;

export const query = graphql`
  fragment PageDefault on Mdx {
    frontmatter {
      path
      name
      title
      tagline
      subtitle
      description
      seoTitle
      thumbnailAlt
      heroAlt
      noIndex
      tags
    }
    thumbnail {
      publicURL
      childImageSharp {
        gatsbyImageData
      }
    }
    tallThumbnail {
      publicURL
      childImageSharp {
        gatsbyImageData
      }
    }
    hero {
      publicURL 
      childImageSharp {
        gatsbyImageData
      }
    }
  }
  fragment Studio on Mdx {
    frontmatter {
      name
      title
      email
      phone
      address1
      address2
      address3
      gmap
      thumbnailAlt
    }
    thumbnail {
      childImageSharp {
        gatsbyImageData
      }
    }
  }
  fragment PostFields on ContentfulPost {
    id
    title
    description
    image {
      title
      gatsbyImageData(placeholder: BLURRED)
    }
    publishedDate
    content {
      childMarkdownRemark {
        timeToRead
      }
    }
    location
  }
  fragment CaseStudyPageFields on ContentfulPage {
    id
    title
    productName
    name
    description
    tagline
    image {
      title
      gatsbyImageData(placeholder: BLURRED)
    }
  }
  fragment TileFields on ContentfulTile {
    key: id
    title
    description
    image {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
      title
    }
    backgroundColor: colour
    secondaryColour
    fullWidth
  }
`;
